import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { IOrganization } from 'src/app/models/interfaces/organization.interface';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { OrganizationFormContext } from '../organization-form-context';

@Component({
    selector: 'app-organization-form',
    templateUrl: './organization-form.component.html',
    styleUrls: ['./organization-form.component.scss'],
    imports: [FormsModule, FormFocusDirective, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, NgClass]
})
export class OrganizationFormComponent extends ManagementFormComponent<IOrganization> implements OnInit, OnChanges {
    public faSave = faSave;
    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du client à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('organization')
    public dataInput: Required<IOrganization> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: OrganizationFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<OrganizationFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: OrganizationFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('organizationUpdated')
    public dataOutput: EventEmitter<IOrganization> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
        this.formGroup.get('name').disable();
        this.formGroup.get('creationDate').disable();
        this.formGroup.get('updateDate').disable();
        this.formGroup.get('billingCode').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IOrganization> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            name: new UntypedFormControl(null, Validators.required),
            creationDate: new UntypedFormControl(null, Validators.required),
            updateDate: new UntypedFormControl(null),
            billingCode: new UntypedFormControl(null, Validators.required),
            isAllowedToAccessHub4B: new UntypedFormControl(null),
            organizationId: new UntypedFormControl(null),
            archivingEnabled: new UntypedFormControl(null),
            officeDomain: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        if (!this.formGroup.invalid && !this.formGroup.disabled) {
            super.manualSave();
        }
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
